import React, {useState} from "react";
import type {TextFieldProps} from '@mui/material/TextField';
import {Alert, Box, Dialog, DialogActions, DialogContentText, IconButton, TextField} from "@mui/material";
import type {GridFilterInputValueProps} from '@mui/x-data-grid';
import {useGridRootProps} from '@mui/x-data-grid';
import {Sync} from "@mui/icons-material";
import FilterIcon from "~/components/icons/svg/FilterIcon.svg?react";
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

const HTML_ID_PREFIX = 'score-between-filter-';

function validateInput(lowerBound: string | undefined, upperBound: string | undefined): boolean {
    if (lowerBound && (Number(lowerBound) < 0 || Number(lowerBound) > Number(upperBound ?? 100))) return false;
    // Not shorten the statement due to readablity
    // noinspection RedundantIfStatementJS
    if (upperBound && (Number(upperBound) < Number(lowerBound ?? 0) || Number(upperBound) > 100)) return false;
    return true;
}

export const ScoreBetweenFilterComponent: React.FC<GridFilterInputValueProps> = ({
                                                                                     item,
                                                                                     applyValue,
                                                                                     focusElementRef = null
                                                                                 }) => {
    // Taken from https://mui.com/x/react-data-grid/filtering/customization/#multiple-values-operator

    const rootProps = useGridRootProps();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleClear = () => {
        setFilterValueState([undefined, undefined]);
        setIsDialogOpen(false);
    }
    const handleClose = () => setIsDialogOpen(false);
    const handleSave = () => {
        if (Array.isArray(filterValueState)) {
            const [lowerBound, upperBound] = filterValueState;
            setIsApplying(true);
            filterTimeout.current = setTimeout(() => {
                setIsApplying(false);
                applyValue({...item, value: [lowerBound, upperBound]});
            }, rootProps.filterDebounceMs);
        }
        setIsDialogOpen(false);
    };

    const filterTimeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
    const [filterValueState, setFilterValueState] = React.useState<[string | undefined, string | undefined]>(item.value ?? [undefined, undefined]);
    const [applying, setIsApplying] = React.useState(false);
    const filterValueStateIsSet = Array.isArray(filterValueState) && filterValueState.length === 2 && (Boolean(filterValueState[0]) || Boolean(filterValueState[1]));

    React.useEffect(() => {
        return () => {
            clearTimeout(filterTimeout.current);
        };
    }, []);

    React.useEffect(() => {
        const itemValue = item.value ?? [undefined, undefined];
        setFilterValueState(itemValue);
    }, [item.value]);

    const updateFilterValue = (lowerBound: string | undefined, upperBound: string | undefined) => {
        clearTimeout(filterTimeout.current);
        setFilterValueState([lowerBound, upperBound]);
    };
    const handleUpperFilterChange: TextFieldProps['onChange'] = (event) => {
        const newUpperBound = event.target.value.replace(/\D/g, '');
        updateFilterValue(filterValueState[0], String(Number(newUpperBound)) || undefined);
    };
    const handleLowerFilterChange: TextFieldProps['onChange'] = (event) => {
        const newLowerBound = event.target.value.replace(/\D/g, '');
        updateFilterValue(String(Number(newLowerBound)) || undefined, filterValueState[1]);
    };

    const isActive = applying || filterValueStateIsSet;

    return <>
        <Dialog open={isDialogOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby={HTML_ID_PREFIX + "-title"}
                aria-describedby={HTML_ID_PREFIX + "-description"}>
            <DialogTitle id={HTML_ID_PREFIX + "-title"}>
                Filter by score
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={HTML_ID_PREFIX + "-description"}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'end',
                            height: 48,
                            pl: '20px',
                        }}
                    >
                        <TextField
                            name="lower-bound-input"
                            placeholder="From"
                            label="From"
                            variant="standard"
                            value={Number(filterValueState[0]) ? filterValueState[0] : ''}
                            onChange={handleLowerFilterChange}
                            inputRef={focusElementRef}
                            sx={{mr: 2}}
                        />
                        <TextField
                            name="upper-bound-input"
                            placeholder="To"
                            label="To"
                            variant="standard"
                            value={Number(filterValueState[1]) ? filterValueState[1] : ''}
                            onChange={handleUpperFilterChange}
                            InputProps={applying ? {endAdornment: <Sync/>} : {}}
                        />
                    </Box>
                    {!validateInput(filterValueState[0], filterValueState[1]) && <Alert
                        severity="warning"
                        className="m-4"
                    >
                        Invalid filter! Make sure values conform to 0&nbsp;&le;&nbsp;From&nbsp;&le;&nbsp;To&le;&nbsp;100
                    </Alert>}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClear}>Clear</Button>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} autoFocus
                        disabled={!validateInput(filterValueState[0], filterValueState[1])}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
        <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
            <IconButton
                size="small" color={isActive ? "primary" : "default"}
                onClick={() => setIsDialogOpen(true)}
            >
                <FilterIcon fontSize="small"/>
            </IconButton>
        </Box>
    </>;

}
